import { Tag } from "@caprikorn/dynamo-ui";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { CheckBig, CloseMd, FolderClose, LoadingRound } from "@icons";
import type { GetPerimeterExport } from "@services/apiEcms/apiEcms.types";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type { DocumentRow } from "./DocumentsTable.types";

const columnHelper = createColumnHelper<DocumentRow>();

export const useDocumentColumns = (perimeterExtract?: GetPerimeterExport) => {
  const { t } = useCustomTranslation("main");

  const documentColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t("documents.table.header.name"),
        cell: ({ getValue }) => getValue(), // REQUIRED BY CUSTOMIZE, do not remove
        enableSorting: false,
      }),
      ...(perimeterExtract
        ? [
            columnHelper.accessor("status", {
              header: t("documents.table.header.status"),
              enableSorting: false,
              cell: ({ getValue }) => {
                const rowStatus = getValue();

                if (rowStatus === "EMPTY")
                  return (
                    <Tag
                      color="info"
                      icon={FolderClose}
                      iconStyle="start"
                      label={t("documents.table.extractPerimeterStatus.empty")}
                      size="sm"
                    />
                  );

                if (rowStatus === "DONE")
                  return (
                    <Tag
                      color="success"
                      icon={CheckBig}
                      iconStyle="start"
                      label={t("documents.table.extractPerimeterStatus.done")}
                      size="sm"
                    />
                  );

                if (rowStatus === "IN_PROGRESS")
                  return (
                    <Tag
                      color="warning"
                      icon={LoadingRound}
                      iconStyle="start"
                      label={t(
                        "documents.table.extractPerimeterStatus.inProgress",
                      )}
                      size="sm"
                    />
                  );

                if (rowStatus === "ERROR")
                  return (
                    <Tag
                      color="error"
                      icon={CloseMd}
                      iconStyle="start"
                      label={t("documents.table.extractPerimeterStatus.error")}
                      size="sm"
                    />
                  );

                return null;
              },
            }),
          ]
        : []),
    ],
    [t, perimeterExtract],
  );

  return documentColumns;
};
